import { isValid, parse } from 'date-fns'
import sumBy from 'lodash-es/sumBy'
import uniq from 'lodash-es/uniq'

import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  EMPTY_STRING,
} from '../../../../../constants/common'
import { DATE_API_DATA_FORMAT_SHORT } from '../../../../../constants/date'
import { PAGE_DEFAULT_VALUE } from '../../../../../constants/route'
import { DESC_ORDER_SYMBOL } from '../../../../../constants/sort'
import {
  createNowDateInUkTimezone,
  formatDateToApi,
} from '../../../../../utils/date'
import {
  APPLICANTS_FILTER_PARAM,
  toApplicantListItem,
} from '../../../../RentalPortalApplicant'
import {
  APPLICANT_BOARD_COLUMN_CAP,
  APPLICANT_BOARD_COLUMN_ORDER,
} from '../../RentalPortalApplicantBoard'

import {
  APPLICANTS_API_DAY_BOUNDARY,
  APPLICANTS_API_DEFAULT_ORDERING,
  APPLICANTS_API_LIST_FILTER,
  APPLICANTS_API_ORDERING_FIELD,
  APPLICANTS_API_VALUE_FILTER,
} from './useRentalPortalApplicantsTable.constants'

function toApplicantsApiOrderingEntry(entry) {
  const isDesc = entry.startsWith(DESC_ORDER_SYMBOL)
  const field =
    APPLICANTS_API_ORDERING_FIELD[
      isDesc ? entry.slice(DESC_ORDER_SYMBOL.length) : entry
    ]

  return field
    ? [`${isDesc ? DESC_ORDER_SYMBOL : EMPTY_STRING}${field}`]
    : EMPTY_LIST
}

function toApplicantsApiOrdering(ordering) {
  const apiOrdering = ordering.flatMap(toApplicantsApiOrderingEntry)

  return apiOrdering.length > 0 ? apiOrdering : APPLICANTS_API_DEFAULT_ORDERING
}

function toApplicantsApiDateValue({ param, timezone, value }) {
  const date = parse(
    value,
    DATE_API_DATA_FORMAT_SHORT,
    createNowDateInUkTimezone(timezone)
  )

  return isValid(date)
    ? formatDateToApi(APPLICANTS_API_DAY_BOUNDARY[param](date), timezone)
    : value
}

function toApplicantsApiFilters({ filterValues, timezone }) {
  return Object.entries(filterValues).reduce((acc, [param, value]) => {
    if (APPLICANTS_API_LIST_FILTER[param]) {
      return { ...acc, [APPLICANTS_API_LIST_FILTER[param]]: [value].flat() }
    }

    return APPLICANTS_API_VALUE_FILTER[param]
      ? {
          ...acc,
          [APPLICANTS_API_VALUE_FILTER[param]]: toApplicantsApiDateValue({
            param,
            timezone,
            value,
          }),
        }
      : acc
  }, EMPTY_OBJECT)
}

function getApplicantsBoardStages(filterValues) {
  const stageFilter = filterValues[APPLICANTS_FILTER_PARAM.stage]

  return stageFilter
    ? APPLICANT_BOARD_COLUMN_ORDER.filter(stage => stage === stageFilter)
    : APPLICANT_BOARD_COLUMN_ORDER
}

export function getApplicantsFiltersKey(filterValues) {
  const params = new URLSearchParams()

  Object.entries(filterValues).forEach(([param, value]) =>
    [value].flat().forEach(entry => params.append(param, entry))
  )

  return params.toString()
}

export function getApplicantsFilterValuesFromKey(filtersKey) {
  const params = new URLSearchParams(filtersKey)

  return Object.fromEntries(
    uniq([...params.keys()]).map(param => {
      const values = params.getAll(param)

      return [param, values.length > 1 ? values : values[0]]
    })
  )
}

export function getApplicantsApiBody({
  filterValues,
  ordering,
  page,
  pageSize,
  timezone,
}) {
  return {
    filters: toApplicantsApiFilters({ filterValues, timezone }),
    ordering: toApplicantsApiOrdering(ordering),
    page,
    size: pageSize,
  }
}

export function getApplicantsBoardApiBodies({
  filterValues,
  ordering,
  timezone,
}) {
  const filters = toApplicantsApiFilters({ filterValues, timezone })

  return getApplicantsBoardStages(filterValues).map(stage => ({
    body: {
      filters: { ...filters, stage: [stage] },
      ordering: toApplicantsApiOrdering(ordering),
      page: PAGE_DEFAULT_VALUE,
      size: APPLICANT_BOARD_COLUMN_CAP,
    },
    stage,
  }))
}

export function toApplicantsTableResponse({ data }) {
  const applicants = data.items.map(toApplicantListItem)

  return {
    allItems: applicants,
    items: { items: applicants, permissions: data.permissions },
    pagination: {
      count: data.pagination.total,
      page: data.pagination.page,
      pageCount: Math.max(PAGE_DEFAULT_VALUE, data.pagination.page_count),
      pageSize: data.pagination.size,
    },
  }
}

export function toApplicantsBoardResponse(stageResponses) {
  const stagePages = stageResponses.map(({ response, stage }) => ({
    page: toApplicantsTableResponse(response),
    stage,
  }))

  const applicants = stagePages.flatMap(({ page }) => page.allItems)

  return {
    allItems: applicants,
    items: {
      items: applicants,
      permissions: stagePages[0]?.page.items.permissions ?? EMPTY_OBJECT,
    },
    pagination: {
      count: sumBy(stagePages, ({ page }) => page.pagination.count),
      page: PAGE_DEFAULT_VALUE,
      pageCount: PAGE_DEFAULT_VALUE,
      pageSize: APPLICANT_BOARD_COLUMN_CAP,
    },
    stageTotals: Object.fromEntries(
      stagePages.map(({ page, stage }) => [stage, page.pagination.count])
    ),
  }
}
